import React, { Fragment, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import UncheckedIcon from 'mdi-react/CheckboxBlankOutlineIcon';
import CheckedIcon from 'mdi-react/CheckboxMarkedIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
    fontSize: '12px',
  },
  formLabel: {
    fontSize: '14px'
  },
  menuItem: {
    fontSize: '14px',
    '& .MuiListItem-root': {
      paddingTop: '3px',
      paddingBottom: '3px'
    }
  },
  listItemText: {
    fontSize: '14px',
    '& .MuiListItemText-primary': {
      fontSize: '14px'
    }
  },
  parentText: {
    fontSize: '14px',
    '& .MuiListItemText-primary': {
      fontSize: '14px',
      fontWeight: 600,
    }
  },
  subParentText: {
    fontSize: '13px',
    '& .MuiListItemText-primary': {
      fontSize: '13px',
      fontWeight: 500,
    }
  },
  leafText: {
    fontSize: '12px',
    '& .MuiListItemText-primary': {
      fontSize: '12px',
    }
  },
}));

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 450,
      width: 420,
    },
  },
};

// Recursively flatten tree values to get all leaf nodes
const getAllLeaves = (values) => {
  let leaves = [];
  values.forEach(item => {
    if (item.children && item.children.length > 0) {
      leaves = [...leaves, ...getAllLeaves(item.children)];
    } else if (item.search_value !== "") {
      leaves.push(item);
    }
  });
  return leaves;
};

// Recursively get all leaf filter keys under a parent
const getChildLeaves = (parentItem) => {
  if (!parentItem.children || parentItem.children.length === 0) {
    return parentItem.search_value !== "" ? [parentItem.filter] : [];
  }
  let leaves = [];
  parentItem.children.forEach(child => {
    leaves = [...leaves, ...getChildLeaves(child)];
  });
  return leaves;
};

export default function HierarchicalFilter(props) {
  const classes = props.classes ? props.classes : useStyles();
  const { subFilters, setSubFilter, filter, _filters } = props;

  const [filterValues, setFilterValues] = useState(
    subFilters[filter.search_key] ? subFilters[filter.search_key] : []
  );
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const allLeaves = getAllLeaves(filter.values);

  // Check parent selection state (works for any level)
  const getParentState = (parentItem) => {
    const childLeafKeys = getChildLeaves(parentItem);
    if (childLeafKeys.length === 0) return 'none';
    const selectedCount = childLeafKeys.filter(f => filterValues.includes(f)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === childLeafKeys.length) return 'all';
    return 'indeterminate';
  };

  // Toggle parent: select-all or deselect-all leaf children (works for any level)
  const handleParentToggle = (parentItem) => {
    const childLeafKeys = getChildLeaves(parentItem);
    const state = getParentState(parentItem);
    let newValues = filterValues.slice();
    if (state === 'all') {
      newValues = newValues.filter(f => !childLeafKeys.includes(f));
    } else {
      childLeafKeys.forEach(f => {
        if (!newValues.includes(f)) newValues.push(f);
      });
    }
    setFilterValues(newValues);
    setOpen(true);
  };

  // Toggle a leaf node
  const handleLeafToggle = (filterKey) => {
    let newValues = filterValues.slice();
    const index = newValues.indexOf(filterKey);
    if (index >= 0) {
      newValues.splice(index, 1);
    } else {
      newValues.push(filterKey);
    }
    setFilterValues(newValues);
    setOpen(true);
  };

  // Expand/collapse a group
  const toggleExpand = (e, groupFilter) => {
    e.stopPropagation();
    setExpandedGroups(prev => ({
      ...prev,
      [groupFilter]: !prev[groupFilter]
    }));
  };

  // Commit selection to parent state
  const handleChangeMultiple = () => {
    setOpen(false);
    setSubFilter(filter.search_key, filterValues);
  };

  // Reset all selections
  const handleReset = (event) => {
    event.stopPropagation();
    setFilterValues([]);
    setOpen(true);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  // Get display title for the collapsed select
  const getDisplayTitle = () => {
    const selectedLeaves = allLeaves.filter(leaf =>
      subFilters[filter.search_key] && subFilters[filter.search_key].includes(leaf.filter)
    );
    return selectedLeaves.length > 0 ? [selectedLeaves[0].name] : [];
  };

  // Render a leaf item
  const renderLeaf = (item, paddingLeft, iconSize = 20) => (
    <MenuItem
      key={item.filter}
      className={classes.menuItem}
      onClick={() => handleLeafToggle(item.filter)}
      style={{ paddingLeft }}
    >
      <Checkbox
        checked={filterValues.includes(item.filter)}
        icon={<UncheckedIcon size={iconSize} />}
        checkedIcon={<CheckedIcon size={iconSize} />}
      />
      <ListItemText primary={item.dropdown_name} className={classes.leafText} />
    </MenuItem>
  );

  // Render a parent item with expand/collapse
  const renderParent = (item, paddingLeft, textClass, iconSize = 22) => {
    const state = getParentState(item);
    const isExpanded = !!expandedGroups[item.filter];
    return (
      <MenuItem
        className={classes.menuItem}
        onClick={() => handleParentToggle(item)}
        style={{ paddingRight: 4, paddingLeft }}
      >
        <Checkbox
          checked={state === 'all'}
          indeterminate={state === 'indeterminate'}
          icon={<UncheckedIcon size={iconSize} />}
          checkedIcon={<CheckedIcon size={iconSize} />}
        />
        <ListItemText primary={item.dropdown_name} className={textClass} />
        <IconButton
          size="small"
          onClick={(e) => toggleExpand(e, item.filter)}
          style={{ padding: 4, marginLeft: 'auto' }}
        >
          {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
        </IconButton>
      </MenuItem>
    );
  };

  return (
    <FormControl className={classes.formControl} style={{ minWidth: filter && filter.minWidth ? filter.minWidth : 180 }}>
      <InputLabel id={`${filter.search_key}_filter-label`} className={classes.formLabel}>
        {filter.default_name && !subFilters[filter.search_key] ? filter.default_name : filter.name}
      </InputLabel>
      <Select
        labelId={`${filter.search_key}-label`}
        id={`${filter.search_key}`}
        name={filter.search_key}
        value={subFilters[filter.search_key] ? getDisplayTitle() : []}
        onClose={() => { handleChangeMultiple() }}
        open={open}
        onOpen={handleOpen}
        input={<Input id={`${filter.search_key}`} />}
        renderValue={selected => {
          const count = subFilters[filter.search_key] ? subFilters[filter.search_key].length : 0;
          const style = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 4 };
          if (count > 1) {
            return (
              <div style={style}>
                {selected} <span className="small-text light-text">(+{`${count - 1} More`})</span>
              </div>
            );
          }
          return <div style={style}>{selected}</div>;
        }}
        multiple
        MenuProps={MenuProps}
        className={classes.menuItem}
      >
        {filter.values.map(item => {
          if (item.children && item.children.length > 0) {
            // --- LEVEL 1: CATEGORY ---
            const isCatExpanded = !!expandedGroups[item.filter];
            return (
              <Fragment key={item.filter}>
                {renderParent(item, undefined, classes.parentText, 22)}
                {isCatExpanded && item.children.map(sub => {
                  if (sub.children && sub.children.length > 0) {
                    // --- LEVEL 2: SUBCATEGORY ---
                    const isSubExpanded = !!expandedGroups[sub.filter];
                    return (
                      <Fragment key={sub.filter}>
                        {renderParent(sub, 32, classes.subParentText, 20)}
                        {isSubExpanded && sub.children.map(bc => {
                          if (bc.children && bc.children.length > 0) {
                            // Unlikely but handle deeper nesting
                            return (
                              <Fragment key={bc.filter}>
                                {renderParent(bc, 56, classes.listItemText, 18)}
                              </Fragment>
                            );
                          }
                          // --- LEVEL 3: BUILDING CLASS LEAF ---
                          return renderLeaf(bc, 56, 18);
                        })}
                      </Fragment>
                    );
                  } else if (sub.search_value !== "") {
                    // Level 2 leaf (no grandchildren)
                    return renderLeaf(sub, 32, 20);
                  }
                  return null;
                })}
              </Fragment>
            );
          } else if (item.search_value !== "") {
            // --- LEVEL 1: LEAF-ONLY NODE ---
            return renderLeaf(item, undefined, 22);
          }
          return null;
        })}
        <div style={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 1 }}>
          <hr style={{ margin: 0 }} />
          <div style={{ padding: '8px 16px', textAlign: 'right' }}>
            <Tooltip title="Reset filter">
              <Button onClick={handleReset} size="small" style={{ marginRight: 5 }}>
                Reset
              </Button>
            </Tooltip>
            <Tooltip title="Apply filter">
              <Button onClick={handleChangeMultiple} variant="outlined" size="small">
                Apply
              </Button>
            </Tooltip>
          </div>
        </div>
      </Select>
    </FormControl>
  );
}
